import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchAnalytics } from "../lib/api";
import AnalyticsSection from "../components/AnalyticsSection";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, AreaChart, Area
} from "recharts";

const ORDERS_BY_CITY = [
  { city: "Bangalore", orders: 420 },
  { city: "Mumbai", orders: 380 },
  { city: "Delhi", orders: 360 },
  { city: "Hyderabad", orders: 290 },
  { city: "Pune", orders: 220 },
  { city: "Chennai", orders: 250 },
];

const GROWTH = [
  { m: "Jan", v: 110 },
  { m: "Feb", v: 145 },
  { m: "Mar", v: 168 },
  { m: "Apr", v: 192 },
  { m: "May", v: 230 },
  { m: "Jun", v: 268 },
  { m: "Jul", v: 305 },
  { m: "Aug", v: 340 },
];

const RATINGS = [
  { d: "Mon", r: 4.5 },
  { d: "Tue", r: 4.6 },
  { d: "Wed", r: 4.4 },
  { d: "Thu", r: 4.7 },
  { d: "Fri", r: 4.8 },
  { d: "Sat", r: 4.9 },
  { d: "Sun", r: 4.7 },
];

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetchAnalytics().then(setStats).catch(() => {});
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="analytics-page" className="bg-ink-900 text-white min-h-screen">
      {/* Hero banner */}
      <section className="relative pt-32 pb-12 grain overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#84CC16]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#A3E635] font-bold">FUDK Analytics</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3"
          >
            Pulse of <span className="text-[#84CC16]">your city's</span> kitchens.
          </motion.h1>
          <p className="mt-4 text-white/70 max-w-2xl">A real-time look at orders, growth, and customer love across the FUDK network.</p>
        </div>
      </section>

      {/* Stats reuse */}
      <AnalyticsSection />

      {/* Charts */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div data-testid="chart-orders-city" className="lg:col-span-7 bg-ink-800 rounded-2xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">Orders by City (today)</h3>
            <span className="text-xs uppercase tracking-[0.25em] text-[#A3E635]">Live</span>
          </div>
          <div className="h-72" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ORDERS_BY_CITY}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="city" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #84CC16", borderRadius: 12 }} />
                <Bar dataKey="orders" fill="#84CC16" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="chart-rating" className="lg:col-span-5 bg-ink-800 rounded-2xl border border-white/10 p-6">
          <h3 className="font-display font-bold text-xl mb-4">Avg Rating This Week</h3>
          <div className="h-72" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={RATINGS}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="d" stroke="rgba(255,255,255,0.6)" />
                <YAxis domain={[4, 5]} stroke="rgba(255,255,255,0.6)" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #84CC16", borderRadius: 12 }} />
                <Line type="monotone" dataKey="r" stroke="#A3E635" strokeWidth={3} dot={{ r: 5, fill: "#84CC16" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="chart-growth" className="lg:col-span-12 bg-ink-800 rounded-2xl border border-white/10 p-6">
          <h3 className="font-display font-bold text-xl mb-4">Monthly Order Growth (in thousands)</h3>
          <div className="h-80" style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={GROWTH}>
                <defs>
                  <linearGradient id="lime" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#84CC16" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#84CC16" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="m" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #84CC16", borderRadius: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#84CC16" strokeWidth={3} fill="url(#lime)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </main>
  );
}
